import { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { motion } from "framer-motion";
import { WhatsappLogo, Envelope, PaperPlaneRight, CheckCircle } from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CONTACT_EMAIL = "contacto@gjcenertec.com";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

export default function Contact() {
  const { t } = useLang();
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    interest: "",
    message: "",
  });

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Structured for future backend connection
    console.log("Form submission:", form);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
    setForm({ name: "", company: "", email: "", interest: "", message: "" });
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#0D9488] mb-4"
          >
            {t.contact.overline}
          </motion.p>
          <motion.h1
            initial="hidden"
            animate="visible"
            custom={1}
            variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#0A192F] max-w-4xl mb-6"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t.contact.headline}
          </motion.h1>
          <motion.p
            initial="hidden"
            animate="visible"
            custom={2}
            variants={fadeUp}
            className="text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl"
          >
            {t.contact.intro}
          </motion.p>
        </div>
      </section>

      {/* Form + Sidebar */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Form */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="lg:col-span-2"
            >
              {submitted ? (
                <div className="flex items-center gap-3 p-6 bg-[#0D9488]/10 border border-[#0D9488]/20" data-testid="contact-success-message">
                  <CheckCircle size={24} weight="fill" className="text-[#0D9488]" />
                  <p className="text-base text-[#0A192F] font-medium">{t.contact.form.success}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-2">
                        {t.contact.form.name}
                      </label>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        data-testid="contact-input-name"
                        className="w-full border-b border-slate-300 bg-transparent py-3 text-[#0A192F] focus:border-[#0D9488] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-2">
                        {t.contact.form.company}
                      </label>
                      <input
                        type="text"
                        value={form.company}
                        onChange={(e) => handleChange("company", e.target.value)}
                        data-testid="contact-input-company"
                        className="w-full border-b border-slate-300 bg-transparent py-3 text-[#0A192F] focus:border-[#0D9488] focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-2">
                        {t.contact.form.email}
                      </label>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        data-testid="contact-input-email"
                        className="w-full border-b border-slate-300 bg-transparent py-3 text-[#0A192F] focus:border-[#0D9488] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-2">
                        {t.contact.form.interest}
                      </label>
                      <Select
                        value={form.interest}
                        onValueChange={(v) => handleChange("interest", v)}
                      >
                        <SelectTrigger
                          data-testid="contact-select-interest"
                          className="w-full border-0 border-b border-slate-300 rounded-none bg-transparent py-3 text-[#0A192F] focus:ring-0 focus:border-[#0D9488] shadow-none h-auto"
                        >
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          {t.contact.form.interestOptions.map((opt) => (
                            <SelectItem key={opt} value={opt} data-testid={`interest-option-${opt}`}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-2">
                      {t.contact.form.message}
                    </label>
                    <textarea
                      rows={5}
                      required
                      value={form.message}
                      onChange={(e) => handleChange("message", e.target.value)}
                      data-testid="contact-input-message"
                      className="w-full border-b border-slate-300 bg-transparent py-3 text-[#0A192F] focus:border-[#0D9488] focus:outline-none transition-colors resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    className="inline-flex items-center bg-[#0A192F] text-white px-7 py-3.5 text-sm font-medium hover:bg-[#0D9488] transition-colors duration-300"
                  >
                    {t.contact.form.submit}
                    <PaperPlaneRight size={18} className="ml-2" />
                  </button>
                </form>
              )}
            </motion.div>

            {/* Sidebar */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={2}
              variants={fadeUp}
              className="space-y-6"
            >
              <a
                href="https://wa.me/00000000000"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="flex items-center gap-3 p-5 bg-white border border-slate-200 hover:shadow-md transition-all duration-300 hover:-translate-y-1"
              >
                <WhatsappLogo size={28} weight="fill" className="text-[#25D366]" />
                <div>
                  <p className="text-sm font-medium text-[#0A192F]">{t.contact.whatsapp}</p>
                  <p className="text-xs text-slate-500">WhatsApp</p>
                </div>
              </a>

              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="contact-email-button"
                className="flex items-center gap-3 p-5 bg-white border border-slate-200 hover:shadow-md transition-all duration-300 hover:-translate-y-1"
              >
                <Envelope size={28} weight="duotone" className="text-[#0D9488]" />
                <div>
                  <p className="text-sm font-medium text-[#0A192F]">{t.contact.email}</p>
                  <p className="text-xs text-slate-500">{CONTACT_EMAIL}</p>
                </div>
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
